import React from "react";
import { LuImage, LuPlus, LuTrash2 } from "react-icons/lu";
import {
  BannerImageFrameControl,
  SlotImageFrameControl,
} from "./BannerImageFrameControl";
import {
  bannerImageFrameStyle,
  normalizeBannerImageFrame,
  type BannerImageFrame,
} from "./templateBannerUtils";
import {
  photoAvaClassForAspect,
  type ImageFrameAspect,
  type ImageFrameShape,
} from "./websiteImageFrameAspect";
import { renderFieldLabel } from "./websiteInspectorFieldLabels";

type UploadHandlers = {
  onFile: (file: File) => void;
  uploading?: boolean;
  /** When set with `src`, shows a Remove control. */
  onRemove?: () => void;
  /** Empty-state CTA. Default: "Upload image". */
  uploadLabel?: string;
  /** Has-image CTA. Default: "Replace image". */
  replaceLabel?: string;
  disabled?: boolean;
  /** Hero Drawer.html — leading + / image icons on upload CTAs. */
  showIcons?: boolean;
  /** Hero Drawer.html — circular trash control instead of "Remove" text. */
  removeIcon?: boolean;
};

/**
 * Shared edit-drawer image block: label → thumb + upload → zoom/crop preview.
 * Crop thumb aspect + shape + fit must match the live frontend frame.
 * Small thumb mirrors pan/zoom when frame props are provided (banners).
 */
export function WebsiteEditorImageEditField({
  label = "Image",
  src,
  aspectRatio,
  shape = "rounded",
  objectFit,
  zoom,
  x,
  y,
  onChange,
  onCommit,
  /** contentEdits slot — when set, frame is read/written via SlotImageFrameControl. */
  slot,
  /** Enable inspector Update when crop/zoom changes (drag does not fire input events). */
  onDirty,
  upload,
  hint,
  className,
  /** Required-field key for Add/Update invalid paint (`data-mte-req-field`). */
  reqField,
  invalid,
}: {
  label?: string;
  src?: string;
  /** CSS aspect-ratio matching the live page frame. */
  aspectRatio: string | ImageFrameAspect;
  /** Outer mask matching the live image container (circle for avatars). */
  shape?: ImageFrameShape;
  /** Match live frame fit — banners use cover (canvas CSS); letterbox only when needed. */
  objectFit?: "cover" | "contain";
  zoom?: number;
  x?: number;
  y?: number;
  onChange?: (frame: BannerImageFrame) => void;
  onCommit?: (frame: BannerImageFrame) => void;
  slot?: string;
  onDirty?: () => void;
  upload?: UploadHandlers | null;
  hint?: React.ReactNode;
  className?: string;
  reqField?: string;
  invalid?: boolean;
}) {
  const image = String(src || "").trim();
  const aspect = String(aspectRatio || "").trim() || "1 / 1";
  const fit = objectFit ?? "cover";
  const hasFrameProps =
    zoom != null || x != null || y != null;
  const thumbFrame = hasFrameProps
    ? normalizeBannerImageFrame({
        imageZoom: zoom,
        imageX: x,
        imageY: y,
      })
    : null;
  const thumbImgStyle: React.CSSProperties = thumbFrame
    ? bannerImageFrameStyle(thumbFrame, { objectFit: fit }) || {
        objectFit: fit,
        objectPosition: "50% 50%",
      }
    : { objectFit: fit, objectPosition: "50% 50%" };
  const avaClass = [
    "mte-we-photo-ava",
    photoAvaClassForAspect(aspect, { shape }),
    fit === "contain" ? "mte-we-photo-ava--contain" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const thumbStyle: React.CSSProperties = {
    aspectRatio: aspect,
  };

  const handleFrameChange = React.useCallback(
    (frame: BannerImageFrame) => {
      onChange?.(frame);
      onDirty?.();
    },
    [onChange, onDirty],
  );

  const uploading = Boolean(upload?.uploading);

  return (
    <div
      className={[
        "mte-we-insp-image",
        uploading ? "is-uploading" : "",
        invalid ? "is-invalid" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-mte-req-field={reqField || undefined}
    >
      {label ? (
        <div className="mte-we-field-label">{renderFieldLabel(label)}</div>
      ) : null}
      {upload ? (
        <div
          className={`mte-we-photo-row${image ? " mte-we-photo-row--with-crop" : ""}`}
        >
          <div className={avaClass} style={thumbStyle}>
            {image ? (
              <img
                src={image}
                alt=""
                draggable={false}
                style={thumbImgStyle}
              />
            ) : null}
            {uploading ? (
              <span className="mte-we-photo-busy" aria-hidden="true">
                <span className="mte-we-photo-spinner" />
              </span>
            ) : null}
          </div>
          <div className="mte-we-photo-actions">
            <label className="mte-we-photo-upload">
              <input
                type="file"
                accept="image/*"
                hidden
                disabled={Boolean(upload.disabled || uploading)}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) upload.onFile(file);
                }}
              />
              {upload.showIcons ? (
                image ? (
                  <LuImage size={14} aria-hidden />
                ) : (
                  <LuPlus size={14} aria-hidden />
                )
              ) : null}
              {image
                ? upload.replaceLabel || "Replace image"
                : upload.uploadLabel || "Upload image"}
            </label>
            {image && upload.onRemove ? (
              <button
                type="button"
                className={`mte-we-photo-remove${
                  upload.removeIcon ? " mte-we-photo-remove--icon" : ""
                }`}
                disabled={Boolean(upload.disabled || uploading)}
                onClick={upload.onRemove}
                title={upload.removeIcon ? "Delete photo" : undefined}
                aria-label={upload.removeIcon ? "Delete photo" : "Remove photo"}
              >
                {upload.removeIcon ? (
                  <LuTrash2 size={15} aria-hidden />
                ) : (
                  "Remove"
                )}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {image ? (
        slot ? (
          <SlotImageFrameControl
            slot={slot}
            src={image}
            aspectRatio={aspectRatio}
            shape={shape}
            objectFit={fit}
            uploading={uploading}
            onDirty={onDirty}
          />
        ) : onChange ? (
          <BannerImageFrameControl
            src={image}
            aspectRatio={aspectRatio}
            shape={shape}
            objectFit={fit}
            uploading={uploading}
            zoom={zoom}
            x={x}
            y={y}
            onChange={handleFrameChange}
            onCommit={onCommit}
          />
        ) : null
      ) : null}
      {hint ? <div className="mte-we-field-hint">{hint}</div> : null}
    </div>
  );
}
